import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon16Image, Icon16Video, Icon16SoundOn, Icon24Transform3dLarge } from '@figma/fpl-icons';
import styled from '@emotion/styled';
import fplTokens from '@figma/fpl-tokens/css-vars';
import { Box } from '@/UI/Box/Box';
import { AppTable } from '@/UI/AppTable/AppTable';
import { AppPaper } from '@/UI/styles';
import { getPlanMetadata, PRICING_COMPARISON_PLAN_ORDER } from '@/consts/pricing.consts';
import { I18N_KEYS } from '@/language/keys';

type CreditsTableData = {
  name: string;
  free: number | '-';
  starter: number;
  pro: number;
  team: number;
  max: number;
};
type CreditsTableProps = { maxWidth?: string | number };

const keys = I18N_KEYS.PAYMENTS.PRICING_TABLE.CREDITS_TABLE;
const planMetadata = getPlanMetadata();

const creditsTableGroups: { category: string; Icon: typeof Icon16Image; models: CreditsTableData[] }[] = [
  {
    category: keys.IMAGE,
    Icon: Icon16Image,
    models: [
      { name: 'GPT Image 2.5', free: 150, starter: 1500, pro: 4000, team: 4500, max: 10000 },
      { name: 'Nano Banana 2.1', free: 21, starter: 214, pro: 571, team: 643, max: 1429 },
      { name: 'Flux 3', free: 75, starter: 750, pro: 2000, team: 2250, max: 5000 },
      { name: 'Flux Fast', free: 375, starter: 3750, pro: 10000, team: 11250, max: 25000 },
      { name: 'Ideogram V4.5', free: 30, starter: 300, pro: 800, team: 900, max: 2000 },
      { name: 'Seedream V5.0', free: 38, starter: 375, pro: 1000, team: 1125, max: 2500 },
      { name: 'Topaz Upscale', free: 8, starter: 79, pro: 211, team: 237, max: 526 },
    ],
  },
  {
    category: keys.VIDEO,
    Icon: Icon16Video,
    models: [
      { name: 'Seedance 2.5', free: '-', starter: 115, pro: 308, team: 346, max: 769 },
      { name: 'Gemini Omni Flash', free: '-', starter: 375, pro: 1000, team: 1125, max: 2500 },
      { name: 'Minimax H3', free: '-', starter: 375, pro: 1000, team: 1125, max: 2500 },
      { name: 'Kling 4', free: '-', starter: 115, pro: 308, team: 346, max: 769 },
      { name: 'Kling 3', free: '-', starter: 122, pro: 324, team: 365, max: 811 },
      { name: 'Happy Horse 1.1', free: '-', starter: 36, pro: 95, team: 107, max: 238 },
      { name: 'Wan 3.0', free: '-', starter: 375, pro: 1000, team: 1125, max: 2500 },
      { name: 'LTX 2.3', free: '-', starter: 250, pro: 667, team: 750, max: 1667 },
      { name: 'Runway Aleph 2.0', free: '-', starter: 3, pro: 8, team: 9, max: 20 },
    ],
  },
  {
    category: keys.THREE_D,
    Icon: Icon24Transform3dLarge,
    models: [
      { name: 'Meshy V6', free: 2, starter: 16, pro: 42, team: 47, max: 104 },
      { name: 'Trellis', free: 5, starter: 50, pro: 133, team: 150, max: 333 },
      { name: 'WorldLabs Marble', free: 1, starter: 10, pro: 28, team: 31, max: 69 },
      { name: 'Tripo H3.1', free: 3, starter: 28, pro: 75, team: 85, max: 189 },
    ],
  },
  {
    category: keys.AUDIO,
    Icon: Icon16SoundOn,
    models: [
      { name: 'Lyria 3', free: 30, starter: 300, pro: 800, team: 900, max: 2000 },
      { name: 'Minimax Speech', free: 15, starter: 150, pro: 400, team: 450, max: 1000 },
      { name: 'Seed Audio 1.0', free: 4, starter: 38, pro: 100, team: 113, max: 250 },
      { name: 'Gemini 3.8 Flash TTS', free: 8, starter: 83, pro: 222, team: 250, max: 556 },
      { name: 'Whisper', free: 3, starter: 30, pro: 80, team: 90, max: 200 },
    ],
  },
];

const numberFormat = new Intl.NumberFormat('en-US');

const StyledTable = styled(AppTable)({
  backgroundColor: 'transparent',
  minWidth: 960,
  tableLayout: 'fixed',
  '& th, & td': {
    borderBottom: 'none',
    backgroundColor: 'transparent',
    fontFamily: 'inherit',
    padding: '8px',
    fontSize: 14,
    lineHeight: '22px',
    fontWeight: 400,
    color: fplTokens.color.text,
  },
  '& thead th': { paddingBottom: 24, fontSize: 13, fontWeight: 500, color: fplTokens.color.textSecondary },
});

const StyledCategoryCell = styled(AppTable.Th)({
  verticalAlign: 'top',
  '&&': { paddingRight: 12 },
  '& svg': { width: 16, height: 16, flexShrink: 0 },
});

function GroupDivider() {
  return (
    <AppTable.Tr aria-hidden="true">
      <AppTable.Td colSpan={7} sx={{ '&&': { padding: '8px 0' } }}>
        <Box sx={{ height: '1px', backgroundColor: fplTokens.color.border }} />
      </AppTable.Td>
    </AppTable.Tr>
  );
}

function CreditCells({ row }: { row: CreditsTableData }) {
  return PRICING_COMPARISON_PLAN_ORDER.map((plan) => (
    <AppTable.Td key={plan} align="center">
      {typeof row[plan] === 'number' ? numberFormat.format(row[plan]) : row[plan]}
    </AppTable.Td>
  ));
}

function CreditsTable({ maxWidth }: CreditsTableProps) {
  const { t } = useTranslation();
  return (
    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
      <AppTable.TableContainer
        component={AppPaper}
        sx={{
          backgroundColor: fplTokens.color.bgElevated,
          boxShadow: 'none',
          maxWidth,
          backgroundImage: 'none',
          border: 'none',
          padding: 3,
        }}
      >
        <StyledTable aria-label={t(keys.ARIA_LABEL)} columnWidths={[110, 260, 118, 118, 118, 118, 118]}>
          <AppTable.Thead>
            <AppTable.Tr>
              <AppTable.Th aria-label={t(keys.CATEGORY)} />
              <AppTable.Th aria-label={t(keys.MODEL)} />
              {PRICING_COMPARISON_PLAN_ORDER.map((plan) => (
                <AppTable.Th key={plan} scope="col" align="center">
                  {t(planMetadata[plan].name)}
                </AppTable.Th>
              ))}
            </AppTable.Tr>
          </AppTable.Thead>
          {creditsTableGroups.map((group, index) => (
            <AppTable.Tbody key={group.category}>
              {index > 0 && <GroupDivider />}
              {group.models.map((row, rowIndex) => (
                <AppTable.Tr key={row.name}>
                  {rowIndex === 0 && (
                    <StyledCategoryCell component="th" scope="rowgroup" rowSpan={group.models.length}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                        <group.Icon aria-hidden="true" />
                        {t(group.category)}
                      </Box>
                    </StyledCategoryCell>
                  )}
                  <AppTable.Th component="th" scope="row">
                    {row.name}
                  </AppTable.Th>
                  <CreditCells row={row} />
                </AppTable.Tr>
              ))}
            </AppTable.Tbody>
          ))}
          <AppTable.Tbody>
            <GroupDivider />
            <AppTable.Tr>
              <AppTable.Td />
              <AppTable.Th component="th" scope="row">
                {t(keys.MONTHLY_CREDITS)}
              </AppTable.Th>
              {PRICING_COMPARISON_PLAN_ORDER.map((plan) => (
                <AppTable.Td key={plan} align="center">
                  {numberFormat.format(planMetadata[plan].monthlyCredits)}
                </AppTable.Td>
              ))}
            </AppTable.Tr>
          </AppTable.Tbody>
        </StyledTable>
      </AppTable.TableContainer>
    </Box>
  );
}

export default CreditsTable;
